import "@modernary/mui/dist/style.css";
import { createApp } from "vue";
import "./styles/tailwind.css";
// import "@modernary/ui-components/style.css";
import { apiHosts } from "@/api/constants/apiHosts";
import { createProPlugin, inputs } from "@formkit/pro";
import { defaultConfig, plugin } from "@formkit/vue";
import Notifications from "notiwind";
import { createPinia } from "pinia";
import InfiniteLoading from "v3-infinite-loading";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App.vue";
import { routeNames, routes } from "./routes";
// import { LE } from "@/constants/enums";
import { default as mui } from "@modernary/mui";
import { useAppSettingsStore } from "@modernary/mui/components/common";
import { bffAuthPlugin, type BffOptions } from "@modernary/mui/utils/authentication";
import { authzPlugin, type AuthorizationClientConfig } from "@modernary/mui/utils/authorization";
import { useGeneratedDocumentConfigStore } from "@modernary/mui/utils/docGen";
import { useIdentityConfigStore } from "@modernary/mui/utils/identity";

import { formKitConfig } from "@/formkit.config";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import { useEntityDocumentConfigStore } from "@modernary/mui/components/document";
import { getLookupsFromLookupGroup } from "@modernary/mui/components/lookup";
import { useEntityNoteConfigStore } from "@modernary/mui/components/note";
import { useToastStore } from "@modernary/mui/components/toasts";
import { LE } from "./constants/enums";

// TODO: Get import from ui-c working.

const app = createApp(App);

/**
 * Specify plugins to load from the /plugins directory
 * Pass file names without any extension like so
 *
 * @example
 *
 * loadPlugins(['vue-fontawesome'])
 */
// loadPlugins([]);

/**
 * Automatically imports and registers base components
 * Make sure you have at least one Base components in components/base directory
 */
// registerBaseComponents(app);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.afterEach((to, from) => {
  const appSettingsStore = useAppSettingsStore();
  if (to.meta.title) {
    document.title = appSettingsStore.pageTitleBase + " - " + to.meta.title;
  } else {
    document.title = appSettingsStore.pageTitleBase;
  }
});

/* TODO: Consider changing to use https://router.vuejs.org/guide/advanced/navigation-guards.html#Global-Resolve-Guards
 Then we can await the lookups and inject them into the route for use in the component's props. */
router.beforeEach((to, from) => {
  if (to.meta.lookupGroups && Array.isArray(to.meta.lookupGroups)) {
    to.meta.lookupGroups.forEach(async (groupCode) => {
      getLookupsFromLookupGroup(groupCode);
    });
  }
});

// Register 3rd-party components used in ui-components
app.component("infinite-loading", InfiniteLoading);

const pro = createProPlugin("fk-53688fd8db", inputs);
formKitConfig.plugins?.push(pro);
if (formKitConfig.icons)
  formKitConfig.icons.checkCircleIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" /></svg>`;

const pinia = createPinia();
app.use(pinia);
// import pinia plugins.

// App Settings - This must happen after we've created Pinia, and called app.use
const appSettingsStore = useAppSettingsStore();
appSettingsStore.agencyName = "UST";
appSettingsStore.pageTitleBase = "UST";
appSettingsStore.baseURL = import.meta.env.VITE_UST_API_BASE_URL;
appSettingsStore.apiHost = apiHosts.UST;
appSettingsStore.systemIdentifier = "UST";
appSettingsStore.applicationTitle = "Underground Storage Tank System";
appSettingsStore.viteBaseUrl = import.meta.env.BASE_URL;
appSettingsStore.datepickerFormat = "MM/DD/YYYY \\at hh:mm A";
appSettingsStore.ianaSystemTimeZone = "Pacific/Honolulu";
appSettingsStore.timeZoneMap = new Map([["GMT-10", "HST"]]);
// appSettingsStore.defaultAddressCountryMurn = LE.Country.USA;
appSettingsStore.displayTimeZones = true;
appSettingsStore.changePasswordUrl = import.meta.env.VITE_IDADMIN_CHANGE_PASSWORD_URL;

const identityConfigStore = useIdentityConfigStore();
identityConfigStore.setBaseURL(import.meta.env.VITE_IDADMIN_API_BASE_URL);
identityConfigStore.setApiHost(apiHosts.IDADMIN);

// const generatedDocumentConfigStore = useGeneratedDocumentConfigStore();
// generatedDocumentConfigStore.setBaseURL(import.meta.env.VITE_DOCGEN_API_BASE_URL);
// generatedDocumentConfigStore.setApiHost(apiHosts.DOCGEN);

const entityNoteConfigStore = useEntityNoteConfigStore();
entityNoteConfigStore.noteMaxLength = 255;

const entityDocumentConfigStore = useEntityDocumentConfigStore();
entityDocumentConfigStore.undownloadableMessage =
  "This file upload is not complete. Please refresh to update the file attachment status. Contact support if the file does not upload.";
entityDocumentConfigStore.prefersDownloading = false;
entityDocumentConfigStore.documentStatuses = { pending: "Pending", uploaded: "Uploaded", failed: "Failed" } as any;
entityDocumentConfigStore.entityDocumentMap = new Map([
  [
    "facility",
    {
      config: {
        routeNamePrefix: "Facilities.FacilityDocument",
        categoryMurn: LE.DocumentCategory.Facility,
        listName: routeNames.facilities.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "facility",
    },
  ],
  [
    "complianceEvent",
    {
      config: {
        routeNamePrefix: "ComplianceEvent.ComplianceEventDocument",
        categoryMurn: LE.DocumentCategory.ComplianceEvent,
        listName: routeNames.complianceEvent.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "complianceEvent",
    },
  ],
  [
    "permit",
    {
      config: {
        routeNamePrefix: "Permit.PermitDocument",
        categoryMurn: LE.DocumentCategory.Permit,
        listName: routeNames.permit.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "permit",
    },
  ],
  [
    "release",
    {
      config: {
        routeNamePrefix: "Release.ReleaseDocument",
        categoryMurn: LE.DocumentCategory.Release,
        listName: routeNames.release.documents,
      },
      onlyInFormEdit: false,
      murnResourceType: "release",
    },
  ],
  [
    "tank",
    {
      onlyInFormEdit: true,
      murnResourceType: "tank",
    },
  ],
  [
    "notificationForm",
    {
      onlyInFormEdit: true,
      murnResourceType: "notificationForm",
    },
  ],
  [
    "inspectionItem",
    {
      onlyInFormEdit: true,
      murnResourceType: "inspectionItem",
    },
  ],
  [
    "financialAssurance",
    {
      onlyInFormEdit: true,
      murnResourceType: "financialAssurance",
    },
  ],
]);

app.use(router);
app.use(plugin, defaultConfig(formKitConfig));
app.use(Notifications);

app.use(authzPlugin, {
  baseApiUrl: import.meta.env.VITE_AUTHORIZATION_API_BASE_URL,
} as AuthorizationClientConfig);
app.use(mui);
app.use(bffAuthPlugin, {
  registerUrl: import.meta.env.VITE_IDADMIN_REGISTER_URL,
} as BffOptions);
app.use(autoAnimatePlugin);
app.mount("#app");
const toastStore = useToastStore();
toastStore.setup(app);
